import React, {
  createContext,
  useContext,
  useCallback,
  PropsWithChildren,
  useMemo,
  useState,
} from 'react';
import * as Amplitude from '@amplitude/analytics-browser';
import { config } from '../config';
import { Identify } from '@amplitude/analytics-browser';

// Initialize Amplitude for event tracking
Amplitude.init(config.amplitudeKey);

const baseEventProperties = {
  application: 'teams',
  appSource: config.appName,
  appVersion: config.appVersion,
};

type RobinTeamsEvent = string; // Placeholder type

// Interface for the Amplitude Event Logger Context
interface IAmplitudeLoggerContext {
  setUserId: (userId: string) => void;
  setOrgInfo: (orgId: string, orgSlug?: string) => void;
  reportEvent: (
    eventName: RobinTeamsEvent,
    additionalProperties?: Object
  ) => void;
}

const AmplitudeLoggerContext = createContext<
  IAmplitudeLoggerContext | undefined
>(undefined);

export const AmplitudeLoggerProvider: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const [userId, setUserIdState] = useState<string | null>(null);
  const [orgId, setOrgIdState] = useState<string | null>(null);
  const [orgSlug, setOrgSlugState] = useState<string | null>(null);

  // Renamed for clarity
  const setAmplitudeUserId = (newUserId: string) => {
    setUserIdState(newUserId);
    Amplitude.setUserId(newUserId);
  };

  // The slug is unknown when the token's org isn't among the listed orgs.
  const setOrgInfo = (newOrgId: string, newOrgSlug?: string) => {
    setOrgIdState(newOrgId);
    setOrgSlugState(newOrgSlug ?? null);
    const identify = new Identify();
    identify.set('organization_id', newOrgId);
    if (newOrgSlug !== undefined) {
      identify.set('organization_slug', newOrgSlug);
    }

    Amplitude.identify(identify);
  };

  const reportEvent = useCallback(
    (eventName: RobinTeamsEvent, additionalProperties?: Object) => {
      if (config.env === 'development') {
        console.warn(
          `(Dev) AmplitudeLoggerContext: reportEvent ('${eventName}') `
        );
      }
      Amplitude.track(eventName, {
        ...baseEventProperties,
        ...additionalProperties,
      });
    },
    [orgId, orgSlug]
  );

  const value = useMemo(
    () => ({
      setUserId: setAmplitudeUserId,
      setOrgInfo,
      reportEvent,
    }),
    [setAmplitudeUserId, setOrgInfo, reportEvent]
  );

  return (
    <AmplitudeLoggerContext.Provider value={value}>
      {children}
    </AmplitudeLoggerContext.Provider>
  );
};

// Renamed hook to reflect its purpose
export const useAmplitudeLogger = (): IAmplitudeLoggerContext => {
  const context = useContext(AmplitudeLoggerContext);
  if (context === undefined) {
    throw new Error(
      'useAmplitudeLogger must be used within an AmplitudeLoggerProvider'
    );
  }
  return context;
};
